import { RISK_FREE_RATE } from "@/lib/constants";
import { layerColor } from "@/lib/palette";
import { getRegistryEntry } from "@/lib/strategy/registry";
import { WORKSPACE_ROUTES } from "@/lib/workspace-routes";
import type { StrategyLayer } from "@/types/strategy";
import {
  DEFAULT_PRICE_RANGE_PCT,
  OUTPUT_FIELDS,
  SWEEP_FIELDS,
  type ManifoldAxes,
} from "./axes";
import { DEFAULT_MANIFOLD_AXES } from "./presets";

export const MANIFOLD_EXAMPLE_KINDS = [
  "iron_condor",
  "single_call",
  "single_put",
  "bull_call_spread",
  "straddle",
  "iron_fly",
] as const;
export type ManifoldExampleKind = (typeof MANIFOLD_EXAMPLE_KINDS)[number];
export interface ManifoldExample {
  kind: ManifoldExampleKind;
  spot: number;
  iv: number;
  dte: number;
  axes: ManifoldAxes;
}

export function readManifoldExample(search: string): ManifoldExample {
  const params = new URLSearchParams(search);
  const kind = params.get("example") as ManifoldExampleKind;
  const number = (key: string, fallback: number, min: number, max: number) => {
    const raw = params.get(key);
    const value = raw == null || raw.trim() === "" ? NaN : Number(raw);
    return Number.isFinite(value) && value >= min && value <= max
      ? value
      : fallback;
  };
  const axes = { ...DEFAULT_MANIFOLD_AXES };
  for (const field of ["x", "y", "z", "colorBy"] as const) {
    const value = params.get(`example_${field}`);
    if (field === "x" || field === "y") {
      if (SWEEP_FIELDS.includes(value as typeof axes.x))
        axes[field] = value as typeof axes.x;
    } else if (OUTPUT_FIELDS.includes(value as typeof axes.z)) {
      axes[field] = value as typeof axes.z;
    }
  }
  if (axes.x === axes.y) axes.y = axes.x === "dte" ? "price" : "dte";
  return {
    kind: MANIFOLD_EXAMPLE_KINDS.includes(kind) ? kind : "iron_condor",
    spot: Math.round(number("example_spot", 6500, 1000, 10000) / 5) * 5,
    iv: number("example_iv", 0.15, 0.01, 1),
    dte: Math.round(number("example_dte", 30, 1, 90)),
    axes,
  };
}

export function manifoldExampleSearch(example: ManifoldExample): string {
  return new URLSearchParams({
    example: example.kind,
    example_spot: String(example.spot),
    example_iv: String(example.iv),
    example_dte: String(example.dte),
    ...Object.fromEntries(
      Object.entries(example.axes).map(([key, value]) => [
        `example_${key}`,
        value,
      ]),
    ),
  }).toString();
}

export function manifoldExampleLayer(
  example: ManifoldExample,
  label: string,
): StrategyLayer {
  return {
    id: `example-${example.kind}`,
    kind: example.kind,
    params: { ...getRegistryEntry(example.kind).defaultParams(6500, 5) },
    color: layerColor(0),
    enabled: true,
    label,
  };
}

export function manifoldExampleConfig(example: ManifoldExample, label: string) {
  return {
    layers: [manifoldExampleLayer(example, label)],
    evalContext: {
      entrySpot: 6500,
      riskFreeRate: RISK_FREE_RATE,
      entryTimeToExpiry: example.dte / 365,
      defaultDte: example.dte,
      baseSigma: example.iv,
    },
    evalSpot: example.spot,
    priceRangePct: DEFAULT_PRICE_RANGE_PCT,
    iv: example.iv,
    dte: example.dte,
    currentT: example.dte / 365,
    axes: example.axes,
    showCombined: false,
  };
}

// Only resume the known 1.0 destination, with validated example parameters.
export function manifoldExampleReturnTo(value: string | null): string | null {
  if (!value?.startsWith(`${WORKSPACE_ROUTES.workbenchModel}?example=`))
    return null;
  return `${WORKSPACE_ROUTES.workbenchModel}?${manifoldExampleSearch(readManifoldExample(value.slice(value.indexOf("?") + 1)))}`;
}
